// websocket/walk-consent.ts — MV4 (2026-10-03): the WALK half of OWNER RULING 2026-10-02 (memory `feedback_trigger_prompt_5s`): a
// PLAYER-controlled creature's Opportunity Attack is a 5-second prompt to its controller (silence = decline); NPC / GM-controlled watchers
// auto-resolve; campaign encounters only (the relay answers `auto` elsewhere). movement-truth.ts finds the watchers a walk's route leaves
// (plain-walk-scope.ts opportunityLeaveWatchers) and throws WalkConsentNeeded for the player-controlled ones BEFORE anything resolves; the page
// entry then asks here and runs the walk again with the answers.
// REUSES, builds no second UI: A10's prompt (trigger-consent.ts — the controller's card, the relay, the 5 s window) and A11's question list
// (attack-truth.ts opportunityConsentQuestions — the watcher's OA and the mover's own damage-taken reactions a swing could set off).
// Registered (not imported by movement-truth.ts) because both of those modules import movement-truth.ts.
import { registerWalkConsentAsker } from './movement-truth.js';
import { askTriggerConsent, createTriggerConsent, triggerController, type PageTriggerConsent } from './trigger-consent.js';
import { opportunityConsentQuestions } from './attack-truth.js';

type Row = Record<string, unknown>;
const upper = (value: unknown): string => String(Array.isArray(value) ? value[0] : value ?? '').trim().toUpperCase();

// MV5 (coordinator Codex Round 23 P2 — a decline is the CONTROLLER's decision, provably): the relay ask id each watcher's Opportunity Attack
// question went out under, read off A10's own `ft:trigger-consent` events while this walk's round is open ('asked' names the question key and
// the ask id; 'settled' names the key's reactor and reaction). A decline posted to the legacy endpoint carries it (`WATCHER@ASK`), and PHP
// honours it only when the relay recorded that controller's "no" or ≥ 5 s of silence for that very ask (ft_walk_oa_declines.php).
const OA_ID = 'D1EADC34-832C-1909-2FC3-722AC857FBEA';
const askIds = new WeakMap<object, Map<string, string>>();

registerWalkConsentAsker({
  needsAsk: (encounterId, watcher) => triggerController(encounterId, watcher) !== null,
  create: encounterId => createTriggerConsent(encounterId),
  ask: async (encounterId, mover, watchers, consent) => {
    const moverRow = mover as unknown as Row;
    for (const watcherId of watchers) for (const question of opportunityConsentQuestions(encounterId, moverRow, watcherId)) consent.need(question);
    const keyAsk = new Map<string, string>();
    const byWatcher = askIds.get(consent) ?? new Map<string, string>();
    askIds.set(consent, byWatcher);
    const listen = (event: Event): void => {
      const d = (event as CustomEvent<Row>).detail ?? {};
      const key = String(d.key ?? '');
      if (d.phase === 'asked' && key && (d.route === 'asked') && typeof d.askId === 'string') keyAsk.set(key, d.askId);
      if (d.phase === 'settled' && key && upper(d.abilityId) === OA_ID && keyAsk.has(key)) byWatcher.set(upper(d.reactorId), keyAsk.get(key)!);
    };
    window.addEventListener('ft:trigger-consent', listen);
    // askTriggerConsent fixes the answers on a PageTriggerConsent (the reader this module creates); a foreign reader is never passed here
    try { await askTriggerConsent(encounterId, upper((moverRow.encounter_creatures as Row | undefined)?.encounter_creature_id), consent as PageTriggerConsent); }
    finally { window.removeEventListener('ft:trigger-consent', listen); }
  },
  askIdFor: (consent, watcherId) => askIds.get(consent)?.get(upper(watcherId)),
});
